/**
 * Quadro kanban de requisições — compartilhado por Compra e Separação.
 * Redesenho ADR-0069 (2026-09-14): grid fluido SEM scroll lateral; coluna
 * com `visivel: false` vira uma tira de 44px (clique expande); o conteúdo
 * do card pode ser trocado por `renderCard(req, coluna)` — Compras usa a
 * barra de itens, prazo e avatar; Separação fica com o card padrão.
 *
 * A máquina de estados NÃO mora aqui: quem decide a transição ao soltar é o
 * pai (`onSoltar(req, coluna)`).
 *
 * No celular (≤ 768px) vira "lista por status": um seletor de raia acima
 * (chip por coluna, com a contagem) e UMA raia na largura toda. Quatro
 * colunas de 130px mostravam "P…", "E…", "S…", "C…" (auditoria mobile
 * 2026-09-24). Arrastar não existe no toque — mover continua pelo detalhe.
 */
import React, { useState } from "react";

import Checkbox from "../../../../components/Checkbox";
import useMediaQuery from "../../../../hooks/useMediaQuery";
import { mq } from "../../../../styles/canonical/breakpoints";
import {
  Board, Chip, KCard, KMeta, KNum, KTitle, Lane, LaneBody, LaneCount, LaneDot, LaneHead,
  LaneRecolhida, LaneTabs,
} from "./styles";

/** Coluna onde o card cai: 1o por `etapa_quadro` (se a coluna existe), senao a
 *  1a coluna que ancora a `situacao` atual. Desacoplado (igual Solicitacoes). */
export function colunaDoCard(req, colunas) {
  if (req.etapa_quadro) {
    const porEtapa = colunas.find((c) => c.id === req.etapa_quadro);
    if (porEtapa) return porEtapa.id;
  }
  const porSituacao = colunas.find(
    (c) => Array.isArray(c.situacoes) && c.situacoes.includes(req.situacao),
  );
  return porSituacao ? porSituacao.id : (colunas[0]?.id ?? null);
}

function CardPadrao({ r, vocab, campoItens, comRevisao }) {
  return (
    <>
      <KNum>
        <span>{r.numero}</span>
        {comRevisao && r.em_revisao ? (
          <span title="Em revisão pelo solicitante — separação bloqueada">🔒</span>
        ) : null}
      </KNum>
      <KTitle>{r.cliente_nome || "Sem cliente"}</KTitle>
      <KMeta>
        {r.projeto_resumo || "—"} · {r[campoItens]} itens
        {vocab.ORIGEM_LABEL?.[r.origem] ? ` · ${vocab.ORIGEM_LABEL[r.origem]}` : ""}
      </KMeta>
    </>
  );
}

/** Uma raia por vez: chips com contagem escolhem qual. */
function KanbanMobile({ requisicoes, colunas, onAbrir, vocab, campoItens, comRevisao, renderCard, corDoCard }) {
  const contagem = (c) => requisicoes.filter((r) => colunaDoCard(r, colunas) === c.id).length;
  // Abre na 1ª coluna com cards (a fila de quem vai trabalhar), senão na 1ª.
  const [ativaId, setAtivaId] = useState(() => (colunas.find((c) => contagem(c) > 0) || colunas[0])?.id ?? null);
  const ativa = colunas.find((c) => c.id === ativaId) || colunas[0];
  if (!ativa) return null;
  const cards = requisicoes.filter((r) => colunaDoCard(r, colunas) === ativa.id);
  return (
    <div data-testid="kanban-mobile">
      <LaneTabs role="tablist" aria-label="Coluna do quadro">
        {colunas.map((c) => (
          <Chip
            key={c.id}
            type="button"
            role="tab"
            aria-selected={c.id === ativa.id}
            $on={c.id === ativa.id}
            $cor={c.cor}
            onClick={() => setAtivaId(c.id)}
          >
            <span className="dot" /> {c.titulo} <span className="n">{contagem(c)}</span>
          </Chip>
        ))}
      </LaneTabs>
      <Board $cols={1} $recolhidas={0}>
        <Lane>
          <LaneHead>
            <LaneDot $cor={ativa.cor} />
            <span className="titulo">{ativa.titulo}</span>
            <LaneCount>{cards.length}</LaneCount>
          </LaneHead>
          <LaneBody>
            {cards.length === 0 ? (
              <div style={{ fontSize: "var(--text-min)", color: "var(--ink-4)", padding: "12px 4px" }}>Nada nesta coluna.</div>
            ) : null}
            {cards.map((r) => (
              <KCard
                key={r.id}
                $cor={corDoCard ? corDoCard(r, ativa) : ativa.cor}
                onClick={() => onAbrir(r.id)}
                style={{ cursor: "pointer" }}
              >
                {renderCard
                  ? renderCard(r, ativa)
                  : <CardPadrao r={r} vocab={vocab} campoItens={campoItens} comRevisao={comRevisao} />}
              </KCard>
            ))}
          </LaneBody>
        </Lane>
      </Board>
    </div>
  );
}

export default function Kanban({
  requisicoes, colunas, onSoltar, onAbrir, podeGerir,
  vocab = {}, campoItens = "qtd_itens", comRevisao = false,
  renderCard, hintColuna, corDoCard,
  // Seleção de cards (opcional): `selecionadas` é um Set de ids. A caixa
  // chega ao `renderCard` como 3º argumento, para o card decidir onde pôr.
  selecionadas = null, onToggleSel = null,
}) {
  const mobile = useMediaQuery(mq.mobile);
  const comSelecao = Boolean(podeGerir && selecionadas && onToggleSel);
  const caixaDe = (r) => (comSelecao ? (
    <span className="ksel" data-on={selecionadas.has(r.id) || undefined} onClick={(e) => e.stopPropagation()}>
      <Checkbox
        checked={selecionadas.has(r.id)}
        onChange={() => onToggleSel(r.id)}
        aria-label={`Selecionar ${r.numero}`}
      />
    </span>
  ) : null);
  const [arrastando, setArrastando] = useState(null); // req sendo arrastada
  const [over, setOver] = useState(null); // id da coluna sob o cursor
  const [expandidas, setExpandidas] = useState(() => new Set());

  if (mobile) {
    return (
      <KanbanMobile
        requisicoes={requisicoes}
        colunas={colunas}
        onAbrir={onAbrir}
        vocab={vocab}
        campoItens={campoItens}
        comRevisao={comRevisao}
        renderCard={renderCard}
        corDoCard={corDoCard}
      />
    );
  }

  function soltar(coluna) {
    const req = arrastando;
    setArrastando(null);
    setOver(null);
    if (!req || !podeGerir) return;
    onSoltar(req, coluna);
  }

  const visiveis = colunas.filter((c) => c.visivel !== false || expandidas.has(c.id));
  const recolhidas = colunas.filter((c) => c.visivel === false && !expandidas.has(c.id));

  function propsDrop(coluna) {
    return {
      onDragOver: (e) => { if (podeGerir) { e.preventDefault(); setOver(coluna.id); } },
      onDragLeave: () => setOver((o) => (o === coluna.id ? null : o)),
      onDrop: () => soltar(coluna),
    };
  }

  return (
    <Board $cols={visiveis.length} $recolhidas={recolhidas.length}>
      {visiveis.map((coluna) => {
        const cards = requisicoes.filter((r) => colunaDoCard(r, colunas) === coluna.id);
        return (
          <Lane key={coluna.id} $over={over === coluna.id} {...propsDrop(coluna)}>
            <LaneHead
              title={coluna.visivel === false ? "Clique para recolher" : undefined}
              onClick={coluna.visivel === false ? () => setExpandidas((s) => { const n = new Set(s); n.delete(coluna.id); return n; }) : undefined}
              style={coluna.visivel === false ? { cursor: "pointer" } : undefined}
            >
              <LaneDot $cor={coluna.cor} />
              <span className="titulo">{coluna.titulo}</span>
              <LaneCount>{cards.length}</LaneCount>
              {hintColuna?.(coluna) ? <span className="hint">{hintColuna(coluna)}</span> : null}
            </LaneHead>
            <LaneBody>
              {cards.map((r) => (
                <KCard
                  key={r.id}
                  $cor={corDoCard ? corDoCard(r, coluna) : coluna.cor}
                  // Em revisao nao arrasta: mover de coluna dispara transicao
                  // que o backend recusaria — melhor nem deixar tentar.
                  draggable={podeGerir && !(comRevisao && r.em_revisao)}
                  onDragStart={() => setArrastando(r)}
                  onDragEnd={() => { setArrastando(null); setOver(null); }}
                  onClick={() => onAbrir(r.id)}
                  data-selecionado={comSelecao && selecionadas.has(r.id) ? "true" : "false"}
                >
                  {renderCard
                    ? renderCard(r, coluna, caixaDe(r))
                    : <CardPadrao r={r} vocab={vocab} campoItens={campoItens} comRevisao={comRevisao} />}
                </KCard>
              ))}
            </LaneBody>
          </Lane>
        );
      })}
      {recolhidas.map((coluna) => {
        const n = requisicoes.filter((r) => colunaDoCard(r, colunas) === coluna.id).length;
        return (
          <LaneRecolhida
            key={coluna.id}
            type="button"
            $over={over === coluna.id}
            title={`${coluna.titulo} (${n}) — clique para expandir`}
            onClick={() => setExpandidas((s) => new Set(s).add(coluna.id))}
            {...propsDrop(coluna)}
          >
            <LaneDot $cor={coluna.cor} />
            <LaneCount>{n}</LaneCount>
            <span className="titulo">{coluna.titulo}</span>
          </LaneRecolhida>
        );
      })}
    </Board>
  );
}
